"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.browserAuthProvider = void 0;
const __1 = require("../..");
const config_1 = require("../../config");
const guards_1 = require("../../guards");
const helpers_1 = require("../../routing/helpers");
const embeddedAuthProvider_1 = require("./utils/embeddedAuthProvider");
const isUserData = (0, helpers_1.unsafePayloadValidator)();
const browserAuthProvider = ({ window, configSpace }) => (configProvider) => {
    const config = configProvider[(0, guards_1.ifDefined)(configSpace, 'auth')];
    const accountsBase = (0, __1.once)(() => (0, config_1.resolveConfigValue)(config.accountsBase));
    const queryString = window.location.search;
    const queryKey = 'auth';
    const authQuery = new URLSearchParams(queryString).get(queryKey);
    const referrer = window.document.referrer ? new URL(window.document.referrer) : undefined;
    const isEmbedded = window.parent !== window;
    const trustedParent = isEmbedded && referrer && referrer.hostname.match(/^(openstax\.org|((.*)(\.openstax\.org|local|localhost)))$/) ? referrer : undefined;
    const { embeddedQueryValue, getAuthorizedEmbedUrl } = (0, embeddedAuthProvider_1.embeddedAuthProvider)(() => getUserData(), { queryKey, window });
    let userData = {
        token: [null, embeddedQueryValue].includes(authQuery) ? null : authQuery
    };
    const getAuthorizedLinkUrl = (urlString) => {
        const url = new URL(urlString);
        if (userData.token) {
            url.searchParams.set(queryKey, userData.token);
        }
        return url.href;
    };
    const getAuthorizedUrl = (urlString) => {
        const url = new URL(urlString);
        if (authQuery) {
            url.searchParams.set(queryKey, authQuery);
        }
        return url.href;
    };
    // *note* that this does not actually prevent cookies from being sent on same-origin
    // requests, i'm not sure if its possible to stop browsers from sending cookies in
    // that case
    const getAuthorizedFetchConfigFromData = (data) => {
        const { token } = data;
        return token ? {
            headers: { Authorization: `Bearer ${token}` },
        } : {
            credentials: 'include',
        };
    };
    const getAuthorizedFetchConfig = async () => {
        return getAuthorizedFetchConfigFromData(userData.token ? userData : await getUserData());
    };
    /*
     * requests user identity from parent window via postMessage
     */
    const getParentWindowUser = () => new Promise((resolve, reject) => {
        if (!window.parent || !trustedParent) {
            return reject(new Error('parent window is undefined or not trusted'));
        }
        const handler = (event) => {
            if (event.data.type === embeddedAuthProvider_1.PostMessageTypes.ReceiveUser && event.origin === trustedParent.origin) {
                clearTimeout(timeout);
                window.removeEventListener('message', handler);
                resolve(event.data.userData);
            }
        };
        window.addEventListener('message', handler);
        window.parent.postMessage({ type: embeddedAuthProvider_1.PostMessageTypes.RequestUser }, trustedParent.origin);
        const timeout = setTimeout(() => {
            window.removeEventListener('message', handler);
            reject(new Error('loading user identity timed out'));
        }, 5000);
    });
    /*
     * requests user identity from accounts api using given token or cookie
     */
    const getFetchUser = async () => {
        const response = await window.fetch((await accountsBase()).replace(/\/+$/, '') + '/api/user?always_200=true', getAuthorizedFetchConfigFromData(userData));
        if (response.status === 200) {
            const body = await response.json();
            const user = isUserData(body) ? body : undefined;
            return { ...userData, user };
        }
        const message = await response.text();
        throw new Error(`Error response from Accounts ${response.status}: ${message}`);
    };
    const getUserData = (0, __1.once)(async () => {
        userData = authQuery === embeddedQueryValue
            ? await getParentWindowUser()
            : await getFetchUser();
        return userData;
    });
    const getUser = async () => {
        return (await getUserData()).user;
    };
    return {
        /**
         * adds auth parameters to the url. this is only safe to use when using javascript to navigate
         * within the current window, eg `window.location = 'https://my.otherservice.com';` anchors
         * should use getAuthorizedLinkUrl for their href.
         *
         * result unreliable unless `getUser` is resolved first.
         */
        getAuthorizedUrl,
        /**
         * all link href-s must be rendered with auth tokens so that they work when opened in a new tab
         *
         * result unreliable unless `getUser` is resolved first.
         */
        getAuthorizedLinkUrl,
        /**
         * gets an authorized url for an iframe src. sets params on the url and saves its
         * origin to trust releasing user identity to it
         */
        getAuthorizedEmbedUrl,
        /**
         * gets second argument for `fetch` that has authentication token or cookie
         */
        getAuthorizedFetchConfig,
        /**
         * loads current user identity. does not reflect changes in identity after being called the first time.
         */
        getUser
    };
};
exports.browserAuthProvider = browserAuthProvider;
